import { memo, useMemo } from 'react'
import { cn } from '@/utils/cn'
import { STRIP_BARS, stripBars } from './voiceprint-format'

export interface WaveStripProps {
  /** The peaks the read carried, 0..1; null when it carried none: a plain track is drawn. */
  peaks: readonly number[] | null
  /** How much of the strip is played, 0..1: that part is copper. Left out, none is. */
  played?: number
  /** How many bars there is room for. */
  bars?: number
  /**
   * The strip's height (`h-8` unless given) and anything of its place in the row. A strip that
   * is only looked at may draw finer bars: `[--wave-bar:3px]` sets the widest a bar gets.
   */
  className?: string
}

/**
 * One layer of bars: never wider than 6 px (or the strip's `--wave-bar`), so a wide strip
 * spreads them instead of fattening them.
 */
const BarLayer = memo(function BarLayer({
  levels,
  tone,
}: {
  levels: readonly number[]
  tone: string
}) {
  return (
    <div className="flex h-full items-center justify-between gap-px">
      {levels.map((level, index) => (
        <span
          key={index}
          style={{ height: `${Math.round(Math.max(0.08, level) * 100)}%` }}
          className={cn('max-w-[var(--wave-bar,6px)] min-w-0 flex-1 rounded-full', tone)}
        />
      ))}
    </div>
  )
})

/** A strip without peaks: one thin track, so its length and the played part still read. */
function PlainTrack({ tone }: { tone: string }) {
  return (
    <div className="flex h-full items-center">
      <span className={cn('h-0.5 w-full rounded-full', tone)} />
    </div>
  )
}

/**
 * A waveform as bars: where there is speech in a clip, and how far it has been played. The bars
 * are drawn twice — a muted layer and a copper one clipped to the played part — so a playback
 * tick only moves the clip edge. Bars only, one tone: it is never a trace, and never coloured by
 * level. It says nothing of how alike two voices are, so two strips are not set to be compared
 * by eye. Decorative; the transport or the row beside it carries the words.
 */
export function WaveStrip({ peaks, played = 0, bars = STRIP_BARS, className }: WaveStripProps) {
  const levels = useMemo(() => (peaks ? stripBars(peaks, bars) : null), [peaks, bars])
  const share = Math.min(1, Math.max(0, played))
  const layer = (tone: string) =>
    levels ? <BarLayer levels={levels} tone={tone} /> : <PlainTrack tone={tone} />
  return (
    <div
      aria-hidden
      data-peaks={levels ? 'bars' : 'none'}
      className={cn('relative h-8', className)}
    >
      {layer('bg-muted-foreground/60')}
      {share > 0 && (
        <div
          className="absolute inset-0"
          style={{ clipPath: `inset(0 ${(1 - share) * 100}% 0 0)` }}
        >
          {layer('bg-primary')}
        </div>
      )}
    </div>
  )
}
